import Link from 'next/link';
import { Trans, useTranslation } from 'next-i18next';
import type { MouseEvent } from 'react';
import { useState } from 'react';

import { V1A_NAMESPACE } from '../constants';

const FAQ_COUNT = 7;
const FAQ_ITEMS = Array.from({ length: FAQ_COUNT }, (_value, index) => index);
const TRUST_ROWS = ['anticheat', 'files', 'layer', 'signature', 'teams'] as const;

/**
 * One answer open at a time, the first one open on arrival (the design's `<details name>` group), kept in
 * React state so every engine behaves the same and the summary stays the native disclosure control.
 */
export function Faq() {
  const { t } = useTranslation(V1A_NAMESPACE);
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (index: number) => (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setOpenIndex((current) => (current === index ? null : index));
  };

  return (
    <section className='v1-faq relative isolate overflow-x-clip border-t border-[rgb(255_255_255/.08)] bg-[linear-gradient(180deg,#0B0D10,#0D1015)] py-[clamp(3rem,1.5rem_+_5vw,6rem)]' aria-labelledby='v1-faq-title'>
      <div className='v1-faq__bg' aria-hidden='true' />
      <div className='v1-faq__inner mx-auto grid w-full max-w-[90rem] items-start gap-[clamp(1.75rem,1rem_+_2.5vw,3.5rem)] px-[var(--gutter)] *:min-w-0 lg:grid-cols-[minmax(0,24rem)_minmax(0,1fr)] lg:gap-[clamp(2.5rem,1rem_+_4vw,5rem)]'>
        <div className='v1-faq__aside lg:sticky lg:top-[calc(var(--header-h)_+_2rem)]'>
          <p className='v1-eyebrow m-0 inline-flex items-center gap-2 text-[length:var(--t-micro)] font-semibold uppercase leading-[1.3] tracking-[.16em] text-[#9AA3B5]' data-reveal>
            <span className='v1-eyebrow__dot size-1.5 flex-none rounded-full bg-[#93FF18] shadow-[0_0_10px_rgb(147_255_24/.8)]' aria-hidden='true' />
            {t('faq.eyebrow')}
          </p>
          <h2 className='v1-h2 mt-3 text-balance text-[length:var(--t-h2)] font-bold leading-[1.04] tracking-[-.032em] text-[#FFFFFF] min-[1920px]:text-[length:3.5rem]' id='v1-faq-title' data-reveal>
            <Trans t={t} i18nKey='faq.title' components={{ hl: <span className='v1-hl !text-[#93FF18] [text-shadow:0_0_22px_rgb(147_255_24/.3)] forced-colors:!text-[LinkText]' /> }} />
          </h2>
          <p className='v1-lead mt-4 max-w-[44rem] text-pretty text-[length:var(--t-lead)] leading-[1.55] text-[#CBD5E1]' data-reveal>
            {t('faq.lead')}
          </p>

          <div className='v1-trust relative mt-[clamp(1.5rem,1rem_+_1.5vw,2rem)] overflow-hidden px-[1.125rem] pb-[1.125rem] pt-4 *:relative' data-reveal>
            <p className='v1-trust__h m-0 flex items-center gap-2.5 text-[length:var(--t-body)] font-bold tracking-[-.01em] text-[#FFFFFF]'>
              <span className='v1-trust__ic' aria-hidden='true'>
                <svg viewBox='0 0 24 24' width='18' height='18' fill='none' stroke='currentColor' strokeWidth='1.7' strokeLinecap='round' strokeLinejoin='round' focusable='false'>
                  <path d='M12 3 4.5 6v5.3c0 4.6 3.1 8.7 7.5 9.7 4.4-1 7.5-5.1 7.5-9.7V6L12 3Z' />
                  <path d='m9.1 12.3 2 2 3.9-4.4' />
                </svg>
              </span>
              {t('faq.trust.title')}
            </p>
            {/* role=list keeps list semantics that list-style:none removes in Safari/VoiceOver */}
            {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
            <ul className='v1-trust__list mx-0 mb-0 mt-3.5 grid list-none gap-2 !px-0 !pb-0 !pt-3 *:min-w-0 [&_b]:text-end [&_b]:font-semibold [&_li]:flex [&_li]:flex-wrap [&_li]:items-baseline [&_li]:justify-between [&_li]:gap-3 [&_li]:text-[length:var(--t-micro)] [&_span]:tracking-[.04em] [&_span]:text-[#8C95A8]' role='list'>
              {TRUST_ROWS.map((row) => (
                <li key={row} className={row === 'teams' ? 'is-on' : undefined}>
                  <span>{t(`faq.trust.rows.${row}.label`)}</span>
                  <b className={row === 'teams' ? 'text-[#D4FF27]' : 'text-[#F4F6F7]'}>{t(`faq.trust.rows.${row}.value`)}</b>
                </li>
              ))}
            </ul>
            <p className='v1-trust__foot mt-3.5 flex items-center gap-2 border-t border-[rgb(255_255_255/.08)] pt-3 text-[length:var(--t-micro)] leading-[1.4] text-[#8C95A8]'>
              <span className='v1-trust__dot' aria-hidden='true' />
              {t('faq.trust.foot')}
            </p>
          </div>

          <Link className='v1-faq__support mt-3 flex items-center gap-3 px-4 py-3.5 text-[length:var(--t-body)] !text-[#CBD5E1]' href='/support' target='_blank' rel='noopener' data-reveal>
            <span className='v1-faq__support-ic grid flex-none place-items-center !text-[#93FF18]' aria-hidden='true'>
              <svg viewBox='0 0 24 24' width='18' height='18' fill='none' stroke='currentColor' strokeWidth='1.7' strokeLinecap='round' strokeLinejoin='round' focusable='false'>
                <path d='M21 12a9 9 0 1 0-3.4 7l3.4.9-1-3.3A8.9 8.9 0 0 0 21 12Z' />
                <path d='M9.6 9.6a2.5 2.5 0 1 1 3.4 2.3c-.6.3-1 .9-1 1.6' />
                <path d='M12 16.6v.1' />
              </svg>
            </span>
            <span className='v1-faq__support-txt block min-w-0 flex-1 [&_span]:mt-0.5 [&_span]:block [&_span]:text-[length:var(--t-micro)] [&_span]:leading-[1.4] [&_span]:text-[#8C95A8] [&_strong]:block [&_strong]:font-semibold [&_strong]:tracking-[-.005em] [&_strong]:text-[#FFFFFF]'>
              <strong>{t('faq.support.title')}</strong>
              <span>{t('faq.support.sub')}</span>
            </span>
            <svg className='v1-faq__support-arrow flex-none !text-[#93FF18] rtl:-scale-x-100' viewBox='0 0 24 24' width='16' height='16' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
              <path d='M5 12h14' />
              <path d='m13 6 6 6-6 6' />
            </svg>
            <span className='sr-only'>{t('faq.support.sr')}</span>
          </Link>
        </div>

        <div className='v1-faq__list grid min-w-0 gap-2.5 *:min-w-0' data-reveal-group>
          {FAQ_ITEMS.map((index) => (
            <details key={index} className='v1-acc relative overflow-hidden' open={openIndex === index}>
              <summary className='v1-acc__q flex list-none items-center justify-between gap-3.5 hyphens-auto px-[1.125rem] py-4 text-[length:var(--t-body)] font-semibold tracking-[-.005em] text-[#FFFFFF]' onClick={toggle(index)}>
                <span className='v1-acc__t min-w-0 flex-1'>{t(`faq.items.${index}.q`)}</span>
                <span className='v1-acc__tag flex-none whitespace-nowrap px-[.4375rem] py-[.1875rem] text-[length:var(--t-micro)] font-semibold tracking-[.06em] text-[#D4FF27] max-[480px]:hidden' aria-hidden='true'>
                  {t(`faq.items.${index}.tag`)}
                </span>
                <span className='v1-acc__ico relative size-[1.125rem] flex-none' aria-hidden='true' />
              </summary>
              <div className='v1-acc__a px-[1.125rem] pb-[1.125rem] pt-0 [&_p]:m-0 [&_p]:max-w-[58ch] [&_p]:text-[length:var(--t-body)] [&_p]:leading-[1.6] [&_p]:text-[#9AA3B5]'>
                <p>{t(`faq.items.${index}.a`)}</p>
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
